import { Button, Stack, TypographyProps } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import PageTabsV2 from "@/App/Layouts/PageBody/PageTabsV2";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import AuthorizedElement from "@/common/components/Auth/AuthorizedElement";
import EditIcon from "@/common/components/Icons/Svg/EditIcon";
import { AppModalCloseIconButton } from "@/common/components/Modals/AppModalCloseIconButton";
import { PersistenceKey } from "@/common/constants/persistenceKey";
import { ROUTE_PATH } from "@/common/constants/routing";
import { useBreadcrumbReplacements } from "@/common/contexts/breadcrumbs";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useCurrentTenant } from "@/common/hooks/entity/tenant/useCurrentTenant";
import { useCommonViewParams } from "@/common/hooks/layout/useCommonViewParams";
import { useRealtimeDataUpdates } from "@/common/hooks/realtime/useRealtimeDataUpdates";
import { useAppHistory } from "@/common/hooks/routing/useAppHistory";
import { DataUpdatesChannelName } from "@/common/realtime/dataUpdatesChannelName";
import { ViewVariant } from "@/common/ts/layout";
import { apiClient } from "@/core/api/ApiClient";
import {
  AppPermission,
  DataUpdatesHubClientMethodName,
  EntityType,
  VehicleOperationType,
  WheelOperationDto,
} from "@/core/api/generated/v1";

import { VehicleOperationViewPageHeader } from "../../Vehicle/View/Header/VehicleOperationViewPageHeader";
import BaseEntityView, { BaseEntityViewInheritableProps } from "../../components/BaseEntityView";
import WheelOperationMenu from "../WheelOperationMenu";
import CommentsTabContent from "./Tabs/CommentsTabContent";
import GeneralHistoryTabContent from "./Tabs/GeneralHistoryTabContent";
import InboxTabContent from "./Tabs/InboxTabContent";
import OverviewTabContent from "./Tabs/OverviewTabContent";

export enum WheelOperationViewPageTabs {
  Overview = "Overview",
  Inbox = "Inbox",
  History = "History",
  Comments = "Comments",
}

const defaultDisplayProps = {
  breadcrumbs: true,
  header: true,
  actions: true,
  closeInHeader: true,
  viewVariant: ViewVariant.Page,
};
const defaultHeaderProps = {
  withLink: false,
  typographyProps: undefined as Partial<TypographyProps> | undefined,
};

export interface WheelOperationViewProps extends BaseEntityViewInheritableProps<WheelOperationDto> {
  wheelOperationId: string | null | undefined;
  wheelOperation: WheelOperationDto | null | undefined;
  displayProps?: Partial<typeof defaultDisplayProps>;
  headerProps?: Partial<typeof defaultHeaderProps>;
}

export default function WheelOperationView({
  wheelOperationId,
  wheelOperation,
  displayProps,
  headerProps,
  onUpdate,
  onDelete,
  onClose,
}: WheelOperationViewProps) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };

  const history = useAppHistory();
  const currentTenant = useCurrentTenant();
  const commonViewParams = useCommonViewParams({
    statePersistence: {
      persistenceKey: PersistenceKey.forEntityView(EntityType.WheelOperation),
      viewVariant: displayProps?.viewVariant,
    },
  });

  const wheelOperationRequest = useApiRequest(
    apiClient.wheelOperationsApi.apiV1WheelOperationsWheelOperationIdGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      wheelOperationId: wheelOperationId!,
    },
    {
      deps: [wheelOperationId],
      skip: !wheelOperationId,
    },
  );
  wheelOperation = wheelOperation || wheelOperationRequest?.data;

  useRealtimeDataUpdates({
    channelNames: [
      DataUpdatesChannelName.Entity(
        currentTenant?.id,
        EntityType.WheelOperation,
        wheelOperationId || "",
      ),
    ],
    methodNames: [DataUpdatesHubClientMethodName.EntityChanged],
    handler: undefined,
    entityChangedHandler: (methodName, data) => {
      wheelOperationRequest.handleEntityChanged(data);
    },
  });

  useBreadcrumbReplacements({
    waitTimeout: 10_000,
    idBreadcrumb: wheelOperation && {
      idValue: wheelOperation?.id || "",
      newTitle: wheelOperation?.localNumber || "",
    },
  });

  return (
    <BaseEntityView
      entityType={EntityType.WheelOperation}
      entityId={wheelOperationId}
      entity={wheelOperation}
      entityRequest={wheelOperationRequest}
    >
      <ViewLayout
        displayProps={displayProps}
        header={
          wheelOperation && (
            <VehicleOperationViewPageHeader
              operationType={VehicleOperationType.WheelOperation}
              operationEntity={wheelOperation}
              vehicleId={wheelOperation?.vehicle?.id}
              vehicle={wheelOperation?.vehicle || undefined}
              titleProps={{
                to: headerProps?.withLink
                  ? ROUTE_PATH.WHEEL_OPERATION_VIEW(wheelOperation?.id)
                  : undefined,
                typographyProps: headerProps?.typographyProps,
              }}
              primaryActions={
                displayProps?.actions &&
                wheelOperation && (
                  <Stack direction='row' spacing={1}>
                    <AuthorizedElement permissions={[AppPermission.WheelOperationManage]}>
                      <Button
                        variant='outlined'
                        color='text'
                        component={RouterLink}
                        to={ROUTE_PATH.WHEEL_OPERATION_EDIT(wheelOperation?.id)}
                        startIcon={<EditIcon />}
                      >
                        Edit
                      </Button>
                    </AuthorizedElement>
                  </Stack>
                )
              }
              secondaryActions={
                wheelOperation && (
                  <WheelOperationMenu
                    entity={wheelOperation}
                    displayProps={{
                      actions: {
                        edit: false,
                        delete: true,
                        addToAssignment: true,
                      },
                    }}
                    onUpdate={(newValue) => {
                      newValue
                        ? wheelOperationRequest.replaceData(newValue)
                        : wheelOperationRequest.refetch();
                      onUpdate?.(newValue);
                    }}
                    onDelete={() => (onDelete ? onDelete() : history.goBack())}
                  />
                )
              }
              closeAction={
                displayProps.closeInHeader && onClose ? (
                  <AppModalCloseIconButton onClick={onClose} />
                ) : undefined
              }
              onTagsUpdated={(newValue) => {
                wheelOperationRequest.updateData((x) => {
                  x.tags = newValue || undefined;
                  onUpdate?.(x);
                });
              }}
            />
          )
        }
      >
        <PageTabsV2
          tabIdsDefinition={WheelOperationViewPageTabs}
          defaultTabId={WheelOperationViewPageTabs.Overview}
          commonViewParams={commonViewParams}
          tabs={[
            { label: "Overview", value: WheelOperationViewPageTabs.Overview, isHideable: false },
            { label: "Chats", value: WheelOperationViewPageTabs.Inbox, isHideable: false },
            {
              label: "History",
              value: WheelOperationViewPageTabs.History,
              isHideable: false,
              permissions: [AppPermission.GeneralHistoryRead],
            },
            { label: "Comments", value: WheelOperationViewPageTabs.Comments, isHideable: false },
          ]}
        >
          {({ activeTabId: activeTab }) =>
            wheelOperation && (
              <>
                {activeTab === WheelOperationViewPageTabs.Overview && (
                  <OverviewTabContent
                    onUpdate={(newValue) => {
                      newValue
                        ? wheelOperationRequest.replaceData(newValue)
                        : wheelOperationRequest.refetch();
                      onUpdate?.(newValue);
                    }}
                    wheelOperation={wheelOperation}
                  />
                )}
                {activeTab === WheelOperationViewPageTabs.Inbox && (
                  <InboxTabContent wheelOperation={wheelOperation} />
                )}
                {activeTab === WheelOperationViewPageTabs.History && (
                  <GeneralHistoryTabContent wheelOperation={wheelOperation} />
                )}
                {activeTab === WheelOperationViewPageTabs.Comments && (
                  <CommentsTabContent wheelOperation={wheelOperation} />
                )}
              </>
            )
          }
        </PageTabsV2>
      </ViewLayout>
    </BaseEntityView>
  );
}
